<template>
  <v-form ref="taxiForm" v-model="valid">
    <v-layout style="width: 100%" row wrap>
      <v-flex xs12 pl-2>
        <v-layout pt-2 class="field-padding" row wrap>
          <v-flex xs6 md3 px-2>
            <v-text-field
              :error-messages="errorForField('plate_number')"
              :rules="taxiRules.plate_number"
              maxlength="6"
              required
              v-model="taxi.plate_number"
              label="Plate Number (*)"
              @input="resetError('plate_number')"
              :disabled="disabled"
            />
          </v-flex>
          <v-flex xs6 md3 px-2>
            <v-text-field
              :error-messages="errorForField('registration_number')"
              maxlength="8"
              :rules="taxiRules.registration_number"
              v-model="taxi.registration_number"
              label="Registration Number (*)"
              @input="resetError('registration_number')"
              :disabled="disabled"
            />
          </v-flex>
          <v-flex xs6 md3 px-2>
            <v-text-field
              :error-messages="errorForField('make')"
              maxlength="20"
              :rules="taxiRules.make"
              v-model="taxi.make"
              label="Make (*)"
              @input="resetError('make')"
              :disabled="disabled"
            />
          </v-flex>
          <v-flex xs6 md3 px-2>
            <v-text-field
              :error-messages="errorForField('model')"
              maxlength="20"
              :rules="taxiRules.model"
              v-model="taxi.model"
              label="Model (*)"
              @input="resetError('model')"
              :disabled="disabled"
            />
          </v-flex>
          <v-flex xs6 md3 px-2>
            <v-text-field
              :error-messages="errorForField('colour')"
              maxlength="20"
              :rules="taxiRules.colour"
              v-model="taxi.colour"
              label="Colour (*)"
              @input="resetError('colour')"
              :disabled="disabled"
            />
          </v-flex>
          <v-flex xs6 md6 px-2>
            <v-select
              :error-messages="errorForField('council')"
              :rules="taxiRules.council"
              v-model="taxi.council"
              :items="councils"
              label="Council (*)"
              @input="resetError('council')"
              :disabled="disabled"
            />
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex pt-2 class="field-padding" xs12 pl-2>
        <h4 class="grey--text text--darken-1">Capabilities</h4>
        <v-layout pt-2 row wrap>
          <v-flex xs6 md3 px-2>
            <v-checkbox
              :disabled="disabled"
              required
              v-model="taxi.ppe_barrier"
              color="primary"
              label="PPE Barrier"
            />
          </v-flex>
          <v-flex xs6 md3 px-2>
            <v-text-field
              :error-messages="errorForField('num_seats')"
              :rules="taxiRules.num_seats"
              v-model="taxi.num_seats"
              type="number"
              step="1"
              label="Number of Passengers (*)"
              @input="resetError('num_seats')"
              :disabled="disabled"
            />
          </v-flex>
          <v-flex xs6 md5 px-2>
            <v-select
              :error-messages="errorForField('ride_type_id')"
              :rules="taxiRules.ride_type_id"
              v-model="taxi.ride_type_id"
              :items="rideTypes"
              item-text="name"
              item-value="id"
              label="Ride Type (*)"
              @input="resetError('ride_type_id')"
              :disabled="disabled"
            />
          </v-flex>
          <v-flex xs6 md4 px-2>
            <v-select
              :error-messages="errorForField('type')"
              :rules="taxiRules.type"
              v-model="taxi.type"
              :items="types"
              item-text="name"
              item-value="type"
              label="Body Type (*)"
              @input="resetError('type')"
              :disabled="disabled"
            />
          </v-flex>
          <v-flex xs6 md3 px-2>
            <v-checkbox
              :disabled="disabled"
              required
              v-model="taxi.disabled_access"
              color="primary"
              label="Disabled Access"
            />
          </v-flex>
          <v-flex xs6 md3 px-2>
            <v-checkbox
              :disabled="disabled"
              required
              v-model="taxi.premium_vehicle"
              color="primary"
              label="Premium Vehicle"
            />
          </v-flex>

          <v-flex xs6 md3 px-2>
            <v-checkbox
              :disabled="disabled"
              required
              v-model="taxi.hackney_carriage"
              color="primary"
              label="Hackney Carriage"
            />
          </v-flex>
          <v-flex xs6 md3 px-2>
            <v-checkbox
              :disabled="disabled"
              required
              v-model="taxi.pets_allowed"
              color="primary"
              label="Pets"
            />
          </v-flex>
          <v-flex xs6 md3 px-2>
            <v-checkbox
              :disabled="disabled"
              required
              v-model="taxi.wide"
              color="primary"
              label="Wide Car"
            />
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex class="field-padding" xs12 pl-2>
        <h4 class="grey--text text--darken-1">Certificates and Tax</h4>
        <v-layout pt-2 row wrap>
          <v-flex xs6 md3 px-2>
            <DateInputIcon
              :error-messages="errorForField('mot_valid_until')"
              :rules="taxiRules.mot_valid_until"
              v-model="taxi.mot_valid_until"
              label="MOT Expiry Date"
              @input="resetError('mot_valid_until')"
              :disabled="disabled"
            ></DateInputIcon>
          </v-flex>
          <v-flex xs6 md3 px-2>
            <DateInputIcon
              :error-messages="errorForField('road_tax_valid_until')"
              :rules="taxiRules.road_tax_valid_until"
              v-model="taxi.road_tax_valid_until"
              label="Road Tax Expiry Date"
              @input="resetError('road_tax_valid_until')"
              :disabled="disabled"
            ></DateInputIcon>
          </v-flex>
          <v-flex xs6 md4 px-2>
            <DateInputIcon
              :error-messages="errorForField('compliance_certificate_expiry')"
              :rules="taxiRules.compliance_certificate_expiry"
              v-model="taxi.compliance_certificate_expiry"
              label="Council Compliance Certificate Expiry Date"
              @input="resetError('compliance_certificate_expiry')"
              :disabled="disabled"
            ></DateInputIcon>
          </v-flex>
          <v-flex xs6 md4 px-2>
            <v-text-field
              :error-messages="errorForField('insurance_certificate_number')"
              maxlength="50"
              :rules="taxiRules.insurance_certificate_number"
              v-model="taxi.insurance_certificate_number"
              label="Insurance Certificate Number"
              @input="resetError('insurance_certificate_number')"
              :disabled="disabled"
            />
          </v-flex>
          <v-flex xs6 sm5 md4 px-2>
            <DateInputIcon
              :error-messages="errorForField('insurance_valid_until')"
              :rules="taxiRules.insurance_valid_until"
              v-model="taxi.insurance_valid_until"
              label="Car Insurance Expiry Date"
              @input="resetError('insurance_valid_until')"
              :disabled="disabled"
            ></DateInputIcon>
          </v-flex>
          <v-flex xs6 sm5 md4 px-2>
            <v-text-field
              :error-messages="errorForField('nominal_code')"
              :rules="taxiRules.nominal_code"
              v-model="taxi.nominal_code"
              label="Nominal Code"
              @input="resetError('nominal_code')"
              :disabled="disabled"
            />
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
  </v-form>
</template>

<script>
import DateInputIcon from "@/components/widget/input/DateInputIcon.vue";

export default {
  name: "edit_taxi",
  components: {
    DateInputIcon,
  },
  props: {
    taxi: Object,
    rideTypes: Array,
    councils: Array,
    errors: { type: Object, default: () => {} },
    disabled: Boolean,
  },
  data: function () {
    return {
      valid: false,
      taxiRules: {
        registration_number: [
          (v) => !!v || "Registration number is required",
          (v) =>
            (v && v.length <= 8) ||
            "Registration number cannot exceed 8 characters",
        ],
        plate_number: [
          (v) => !!v || "Plate number is required",
          (v) =>
            (v && v.length <= 6) || "Plate number cannot exceed 6 characters",
        ],
        make: [
          (v) => !!v || "Make is required",
          (v) => (v && v.length <= 20) || "Make cannot exceed 20 characters",
        ],
        model: [
          (v) => !!v || "Model is required",
          (v) => (v && v.length <= 20) || "Model cannot exceed 20 characters",
        ],
        colour: [
          (v) => !!v || "Colour is required",
          (v) => (v && v.length <= 20) || "Colour cannot exceed 20 characters",
        ],
        num_seats: [(v) => !!v || "Number of seats is required"],
        type: [(v) => !!v || "Body type is required"],
        mot_valid_until: [
          (v) =>
            !v ||
            /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/.test(v) ||
            "MOT expiry is in an invalid format",
        ],
        insurance_certificate_number: [],
        insurance_valid_until: [
          (v) =>
            !v ||
            /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/.test(v) ||
            "Insurance expiry is in an invalid format",
        ],
        compliance_certificate_expiry: [
          (v) =>
            !v ||
            /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/.test(v) ||
            "Compliance certificate expiry is in an invalid format",
        ],
        road_tax_valid_until: [
          (v) =>
            !v ||
            /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/.test(v) ||
            "Road tax expiry is in an invalid format",
        ],
        council: [(v) => !!v || "Council is required"],
        ride_type_id: [(v) => !!v || "Ride type is required"],
      },
      types: [
        { name: "Saloon", type: "saloon" },
        { name: "Estate", type: "estate" },
        { name: "Mpv", type: "mpv" },
      ],
    };
  },
  methods: {
    errorForField: function (field) {
      try {
        return this.errors[field];
      } catch (err) {
        return null;
      }
    },
    resetError: function (field) {
      try {
        if (this.errors[field] != "") {
          this.errors[field] = "";
        }
      } catch (err) {}
    },
    validateForm: function () {
      this.$refs.taxiForm.validate();
    },
  },
  watch: {
    disabled: function () {
      this.$refs.taxiForm.reset();
    },
    valid: function () {
      this.$emit("validityChange", this.valid);
    },
  },
};
</script>
